import { useCallback } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { BloxCustomDataProviderType, ApiErrorResponse } from 'utility/types/dataProvider';
import { useNavigate } from 'react-router';
import { useCosmos } from 'providers/contexts/CosmosContext';

export const useMaybeDuplicateChannel = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const navigate = useNavigate();
  const { activeSite } = useCosmos();

  const maybeDuplicateChannel = useCallback(
    async (values: { [key: string]: any }): Promise<boolean> => {
      try {
        const channelId = values.id;

        // Fetch both channel config and map in parallel
        const [configResponse, mapResponse] = await Promise.all([
          dataProvider.getOne('wire/channel', { id: channelId }),
          dataProvider.getCategoryMap(channelId),
        ]);

        const fetchedConfig = configResponse?.data?.data;
        const fetchedMap = mapResponse?.data?.data.section_map;

        if (!fetchedConfig) {
          notify('Failed to load channel configuration', { type: 'error' });
          return false;
        }

        const duplicatePayload = {
          data: {
            driver: fetchedConfig.driver,
            config: {
              ...fetchedConfig.config,
              name: `${fetchedConfig.config.name} (Copy)`,
            },
          },
        };

        const { data: duplicateResponse } = await dataProvider.create(
          'wire/channel',
          duplicatePayload,
        );

        if (duplicateResponse.status === 'success') {
          if (Object.keys(fetchedMap).length > 0) {
            await dataProvider.updateCategoryMap(duplicateResponse.data.id!, fetchedMap);
          }
          notify(`Channel duplicated`, { type: 'success' });
          navigate(`/${activeSite}/feeds/channels/edit/${duplicateResponse.data.id}`);
          return true;
        }

        return false;
      } catch (error: unknown) {
        const castError = error as ApiErrorResponse;
        console.error('[maybeDuplicateChannel]', error);
        const errorMessage = castError?.message || castError?.axiosMessage;
        notify(`Error: ${errorMessage}`, { type: 'error' });
        return false;
      }
    },
    [dataProvider, notify, navigate, activeSite],
  );

  return maybeDuplicateChannel;
};
